import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CardActions,
  Chip,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm, Controller } from 'react-hook-form';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import StatusCard from '../../components/common/StatusCard';
import databaseApi from '../../api/databaseApi';

const DatabasePage = () => {
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnLoading, setIsConnLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [tables, setTables] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [openQueryModal, setOpenQueryModal] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [queryStatus, setQueryStatus] = useState(null);
  const [queryInterval, setQueryInterval] = useState(null);
  const [activeQuery, setActiveQuery] = useState(null);
  
  const { control, handleSubmit, watch, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: '',
      description: '',
      db_type: 'postgresql',
      host: '',
      port: '',
      database: '',
      username: '',
      password: '',
      ssl_enabled: false,
    }
  });
  
  const dbType = watch('db_type');
  
  const dbTypeOptions = [
    { value: 'postgresql', label: 'PostgreSQL', defaultPort: 5432 },
    { value: 'mysql', label: 'MySQL', defaultPort: 3306 },
    { value: 'mssql', label: 'SQL Server', defaultPort: 1433 },
    { value: 'oracle', label: 'Oracle', defaultPort: 1521 },
  ];

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setIsConnLoading(true);
      const response = await databaseApi.listConnections();
      setConnections(response.connections || []);
    } catch (error) {
      console.error('데이터베이스 연결 목록을 가져오는 중 오류 발생:', error);
      setError('데이터베이스 연결 목록을 불러올 수 없습니다.');
    } finally {
      setIsConnLoading(false);
    }
  };

  const fetchTables = async (connectionId) => {
    try {
      setIsTableLoading(true);
      setTables([]);
      const response = await databaseApi.listTables(connectionId);
      setTables(response.tables || []);
      setSelectedConnectionId(connectionId);
    } catch (error) {
      console.error('테이블 목록을 가져오는 중 오류 발생:', error);
      setError('테이블 목록을 불러올 수 없습니다.');
    } finally {
      setIsTableLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await databaseApi.createConnection(data);
      
      // 폼 초기화
      reset();
      
      // 연결 목록 새로고침
      fetchConnections();
      
    } catch (error) {
      console.error('데이터베이스 연결 생성 오류:', error);
      setError('데이터베이스 연결을 생성하는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedConnection) return;
    
    try {
      await databaseApi.deleteConnection(selectedConnection.connection_id);
      setConnections(prevConnections => 
        prevConnections.filter(conn => conn.connection_id !== selectedConnection.connection_id)
      );
      
      if (selectedConnectionId === selectedConnection.connection_id) {
        setSelectedConnectionId(null);
        setTables([]);
      }
      
      setOpenDeleteDialog(false);
      setSelectedConnection(null);
    } catch (error) {
      console.error('데이터베이스 연결 삭제 중 오류 발생:', error);
      setError('데이터베이스 연결을 삭제할 수 없습니다.');
    }
  };

  const handleTestConnection = async (data) => {
    try {
      setIsTesting(true);
      setTestResult(null);
      
      const result = await databaseApi.testConnection(data);
      
      setTestResult({
        success: result.success,
        message: result.message
      });
    } catch (error) {
      console.error('데이터베이스 연결 테스트 오류:', error);
      setTestResult({
        success: false,
        message: '연결 테스트 중 오류가 발생했습니다.'
      });
    } finally {
      setIsTesting(false);
    }
  };

  const executeQuery = async () => {
    if (!selectedConnection || !selectedConnection.connection_id) {
      setQueryStatus({
        status: 'error',
        message: '데이터베이스 연결이 선택되지 않았습니다.'
      });
      return;
    }
    try {
      setIsQueryLoading(true);
      setQueryResult(null);
      setQueryStatus(null);
      // 쿼리 실행 요청
      const response = await databaseApi.executeQuery({
        connection_id: selectedConnection.connection_id,
        query: queryText,
      });
      // 쿼리 ID 저장
      const queryId = response.data.query_id;
      setActiveQuery(queryId);
      setQueryStatus({
        status: response.status,
        message: response.message
      });
      // 쿼리 상태 폴링 시작
      const interval = setInterval(async () => {
        try {
          const statusResponse = await databaseApi.getQueryStatus(queryId);
          setQueryStatus({
            status: statusResponse.status,
            message: statusResponse.message,
            row_count: statusResponse.row_count
          });
          if (statusResponse.status === 'completed') {
            clearInterval(interval);
            setQueryInterval(null);
            const resultResponse = await databaseApi.getQueryResult(queryId);
            setQueryResult(resultResponse);
          } else if (statusResponse.status === 'error') {
            clearInterval(interval);
            setQueryInterval(null);
          }
        } catch (error) {
          console.error('쿼리 상태 확인 중 오류:', error);
          clearInterval(interval);
          setQueryInterval(null);
        }
      }, 1000);
      setQueryInterval(interval);
    } catch (error) {
      console.error('쿼리 실행 중 오류:', error);
      setQueryStatus({
        status: 'error',
        message: '쿼리 실행 중 오류가 발생했습니다.'
      });
    } finally {
      setIsQueryLoading(false);
    }
  };
  
  // 폴링 인터벌 클린업
  useEffect(() => {
    return () => {
      if (queryInterval) {
        clearInterval(queryInterval);
      }
    };
  }, [queryInterval]);

  // SQL 쿼리 예제 컴포넌트 추가
  const SQLQueryExamples = ({ dbType, database, setQueryText }) => {
    const getExamples = () => {
      // WordPress 데이터베이스 쿼리 예제
      if (database && (database.includes('wp') || database.includes('wordpress'))) {
        return [
          { name: 'WordPress 게시물 조회', query: 'SELECT * FROM wp_posts WHERE post_status = \'publish\' LIMIT 10' },
          { name: '게시물과 메타데이터 조인', query: 'SELECT p.ID, p.post_title, p.post_content, p.post_status, p.post_date, m.meta_value FROM wp_posts p JOIN wp_postmeta m ON p.ID = m.post_id WHERE p.post_status = \'publish\' AND m.meta_key = \'_thumbnail_id\' LIMIT 10' },
          { name: '최근 댓글 조회', query: 'SELECT c.comment_ID, c.comment_post_ID, p.post_title, c.comment_author, c.comment_content, c.comment_date FROM wp_comments c JOIN wp_posts p ON c.comment_post_ID = p.ID WHERE c.comment_approved = \'1\' ORDER BY c.comment_date DESC LIMIT 10' }
        ];
      }
      
      // 일반 데이터베이스별 쿼리 예제
      switch (dbType) {
        case 'mysql':
          return [
            { name: '기본 조회', query: 'SELECT * FROM table_name LIMIT 10' },
            { name: '조건부 조회', query: 'SELECT id, name, email FROM users WHERE status = \'active\' LIMIT 10' },
            { name: '조인 예제', query: 'SELECT o.id, c.name, o.order_date, o.total FROM orders o JOIN customers c ON o.customer_id = c.id LIMIT 10' }
          ];
        case 'postgresql':
          return [
            { name: '기본 조회', query: 'SELECT * FROM table_name LIMIT 10' },
            { name: '조건부 조회', query: 'SELECT id, name, email FROM users WHERE status = \'active\' LIMIT 10' },
            { name: 'JSON 데이터 조회', query: 'SELECT id, data->\'name\' as name FROM items WHERE data->>\'active\' = \'true\' LIMIT 10' }
          ];
        case 'sqlserver':
          return [
            { name: '기본 조회', query: 'SELECT TOP 10 * FROM table_name' },
            { name: '조건부 조회', query: 'SELECT TOP 10 id, name, email FROM users WHERE status = \'active\'' },
            { name: '날짜 함수 예제', query: 'SELECT TOP 10 id, name, CONVERT(VARCHAR, created_date, 120) as formatted_date FROM orders' }
          ];
        case 'oracle':
          return [
            { name: '기본 조회', query: 'SELECT * FROM table_name WHERE ROWNUM <= 10' },
            { name: '조건부 조회', query: 'SELECT id, name, email FROM users WHERE status = \'active\' AND ROWNUM <= 10' },
            { name: '날짜 함수 예제', query: 'SELECT id, name, TO_CHAR(created_date, \'YYYY-MM-DD\') as formatted_date FROM orders WHERE ROWNUM <= 10' }
          ];
        case 'mongodb':
          return [
            { name: '기본 조회', query: 'db.collection.find({})' },
            { name: '조건부 조회', query: 'db.users.find({\"status\": \"active\"})' },
            { name: '복합 조건', query: 'db.orders.find({\"total\": {\"$gt\": 100}, \"status\": \"completed\"})' }
          ];
        default:
          return [
            { name: '기본 조회 예제', query: 'SELECT * FROM table_name LIMIT 10' }
          ];
      }
    };

    const examples = getExamples();
    const [expanded, setExpanded] = useState(false);
    
    const handleSetQueryText = (query) => {
      // 쿼리를 클립보드에 복사하고 텍스트 영역에 설정
      navigator.clipboard.writeText(query);
      
      // 모달에서 사용할 경우 부모 컴포넌트에서 받은 setQueryText 함수 사용
      if (setQueryText) {
        setQueryText(query);
      }
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ color: 'primary.main', cursor: 'pointer' }}
          >
            <Typography>쿼리 예제 보기</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mt: 2, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
              {examples.map((example, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {example.name}
                  </Typography>
                  <Box 
                    sx={{ 
                      bgcolor: 'grey.900', 
                      color: 'common.white', 
                      p: 2, 
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'grey.800'
                      }
                    }}
                    onClick={() => handleSetQueryText(example.query)}
                  >
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {example.query}
                    </pre>
                    <Typography variant="caption" sx={{ color: 'info.light', display: 'block', mt: 1 }}>
                      클릭하여 복사하고 적용
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  // 쿼리 결과 표시 컴포넌트
  const QueryResultsView = ({ queryResult }) => {
    if (!queryResult) return null;

    const { columns, rows, query, executed_query } = queryResult;
    
    const showQueryDifference = executed_query && query !== executed_query;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          쿼리 결과
        </Typography>
        
        {showQueryDifference && (
          <Paper sx={{ mb: 3, p: 3, bgcolor: 'background.paper' }} variant="outlined">
            <Typography color="primary" sx={{ mb: 2 }}>
              쿼리가 자동으로 수정되었습니다:
            </Typography>
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  원본 쿼리:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'common.white', borderRadius: 1 }}>
                  <pre style={{ margin: 0, overflowX: 'auto' }}>{query}</pre>
                </Paper>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  실행된 쿼리:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'success.light', borderRadius: 1 }}>
                  <pre style={{ margin: 0, overflowX: 'auto' }}>{executed_query}</pre>
                </Paper>
              </Box>
            </Box>
          </Paper>
        )}
        
        {columns && columns.length > 0 ? (
          <Box sx={{ overflow: 'auto', maxHeight: 300 }}>
            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {columns.map((column, idx) => (
                    <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIdx) => (
                  <TableRow key={rowIdx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                    {row.map((cell, cellIdx) => (
                      <TableCell key={cellIdx}>
                        {cell !== null ? String(cell) : 'NULL'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Alert severity="info">쿼리 결과가 없거나 비어 있습니다.</Alert>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        데이터베이스 연결 관리
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              새 데이터베이스 연결
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: '연결 이름을 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="연결 이름"
                        fullWidth
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="설명 (선택사항)"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="db_type"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>데이터베이스 유형</InputLabel>
                        <Select {...field} label="데이터베이스 유형">
                          {dbTypeOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                
                <Grid item xs={8}>
                  <Controller
                    name="host"
                    control={control}
                    rules={{ required: '호스트 주소를 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="호스트"
                        fullWidth
                        variant="outlined"
                        error={!!errors.host}
                        helperText={errors.host?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={4}>
                  <Controller
                    name="port"
                    control={control}
                    rules={{ required: '포트 번호를 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="포트"
                        fullWidth
                        variant="outlined"
                        type="number"
                        error={!!errors.port}
                        helperText={errors.port?.message}
                        placeholder={dbTypeOptions.find(option => option.value === dbType)?.defaultPort.toString()}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="database"
                    control={control}
                    rules={{ required: '데이터베이스 이름을 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="데이터베이스 이름"
                        fullWidth
                        variant="outlined"
                        error={!!errors.database}
                        helperText={errors.database?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="username"
                    control={control}
                    rules={{ required: '사용자 이름을 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="사용자 이름"
                        fullWidth
                        variant="outlined"
                        error={!!errors.username}
                        helperText={errors.username?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: '비밀번호를 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="비밀번호"
                        fullWidth
                        variant="outlined"
                        type="password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  {testResult && (
                    <Alert 
                      severity={testResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {testResult.message}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleSubmit(handleTestConnection)}
                      disabled={isLoading || isTesting}
                    >
                      {isTesting ? <LoadingSpinner size={24} /> : '연결 테스트'}
                    </Button>
                    
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                    >
                      {isLoading ? <LoadingSpinner size={24} /> : '연결 생성'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
            
            {error && (
              <Box sx={{ mt: 2 }}>
                <ErrorMessage message={error} />
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                데이터베이스 연결 목록
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchConnections}
                disabled={isConnLoading}
              >
                새로고침
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {isConnLoading ? (
              <LoadingSpinner message="데이터베이스 연결 목록을 불러오는 중..." />
            ) : connections.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>연결 없음</AlertTitle>
                아직 생성된 데이터베이스 연결이 없습니다. 좌측 폼에서 새 연결을 생성해주세요.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {connections.map((connection) => (
                  <Grid item xs={12} key={connection.connection_id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" component="div">
                            {connection.name}
                          </Typography>
                          <Chip 
                            label={connection.db_type} 
                            size="small" 
                            color="primary"
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {connection.description || '설명 없음'}
                        </Typography>
                        
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>호스트:</strong> {connection.host}:{connection.port}
                          </Typography>
                          <Typography variant="body2">
                            <strong>데이터베이스:</strong> {connection.database}
                          </Typography>
                          <Typography variant="body2">
                            <strong>사용자:</strong> {connection.username}
                          </Typography>
                        </Box>
                      </CardContent>
                      
                      <CardActions>
                        <Button 
                          size="small" 
                          onClick={() => fetchTables(connection.connection_id)}
                          disabled={isTableLoading}
                        >
                          테이블 보기
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedConnection(connection);
                            setSelectedConnectionId(connection.connection_id);
                            setOpenQueryModal(true);
                          }}
                        >
                          쿼리 실행
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedConnection(connection);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          삭제
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {selectedConnectionId && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  테이블 목록
                </Typography>
                
                {isTableLoading ? (
                  <LoadingSpinner message="테이블 목록을 불러오는 중..." />
                ) : tables.length === 0 ? (
                  <Alert severity="info">
                    <AlertTitle>테이블 없음</AlertTitle>
                    해당 데이터베이스에 테이블이 없거나 접근 권한이 없습니다.
                  </Alert>
                ) : (
                  <Grid container spacing={1}>
                    {tables.map((table) => (
                      <Grid item xs={6} md={4} key={table.name}>
                        <Chip
                          label={table.name}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>데이터베이스 연결 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{selectedConnection?.name}" 연결을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>취소</Button>
          <Button onClick={handleDelete} color="error">삭제</Button>
        </DialogActions>
      </Dialog>
      
      {/* 쿼리 실행 다이얼로그 */}
      <Dialog
        open={openQueryModal}
        onClose={() => {
          if (!isQueryLoading) {
            setOpenQueryModal(false);
            setQueryText('');
            setQueryResult(null);
            setQueryStatus(null);
            if (queryInterval) {
              clearInterval(queryInterval);
              setQueryInterval(null);
            }
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          SQL 쿼리 실행
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            아래 영역에 실행할 SQL 쿼리를 작성하세요. 테이블 목록을 참고하여 쿼리를 작성할 수 있습니다.
          </DialogContentText>
          
          <SQLQueryExamples 
            dbType={selectedConnection?.db_type} 
            database={selectedConnection?.database}
            setQueryText={setQueryText}
          />
          
          <TextField
            label="SQL 쿼리"
            multiline
            rows={5}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="SELECT * FROM table_name"
            sx={{ mb: 2 }}
            disabled={isQueryLoading}
          />
          
          {queryStatus && (
            <Alert 
              severity={queryStatus.status === 'error' ? 'error' : 
                     queryStatus.status === 'completed' ? 'success' : 'info'}
              sx={{ mb: 2 }}
            >
              {queryStatus.message}
              {queryStatus.row_count !== undefined && (
                <span> (총 {queryStatus.row_count}개 행)</span>
              )}
            </Alert>
          )}
          
          {queryResult && (
            <QueryResultsView queryResult={queryResult} />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenQueryModal(false);
              setQueryText('');
              setQueryResult(null);
              setQueryStatus(null);
              if (queryInterval) {
                clearInterval(queryInterval);
                setQueryInterval(null);
              }
            }}
            disabled={isQueryLoading}
          >
            닫기
          </Button>
          <Button 
            onClick={executeQuery}
            variant="contained" 
            color="primary"
            disabled={isQueryLoading || !queryText.trim()}
          >
            {isQueryLoading ? <LoadingSpinner size={24} /> : '쿼리 실행'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabasePage; 